<template>
  <div id="CreateManualOrderView" class="container max-w-4xl mx-auto pt-10 pb-20 px6">
    <div class="text-center">
      <router-link to="/" class="text-blue-500 no-underline hover:underline">
        Scan Out
      </router-link> |
      <router-link to="/tracking" class="text-blue-500 no-underline hover:underline">
        Scan Tracking Number
      </router-link> |
      <router-link to="/uid-code" class="text-blue-500 no-underline hover:underline">
        Search UID Codes
      </router-link> |
      <router-link to="/order-number" class="text-blue-500 no-underline hover:underline">
        Search Order Number
      </router-link>
    </div>
    
    <div v-if="successMessage" class="text-green-700 text-center mt-4">{{ successMessage }}</div>
    <div v-if="errorMessage" class="text-red-700 text-center mt-4">{{ errorMessage }}</div>
    
    <div class="flex flex-wrap mt-4 mb-6">
      <div class="w-full md:w-1/3 px-3">
        <label 
          class="
              block 
              uppercase 
              tracking-wide 
              text-xs 
              font-bold 
              mb-2
              text-gray-900
          "
        >
            Store
        </label>
        <select class="
                appearance-none
                block
                w-full
                bg-white
                text-gray-700
                border
                border-gray-400
                rounded
                py-3
                px-4
                leading-tight
                focus:outline-none
                focus:bg-white
                focus:border-gray-500
                "
                v-model="selStoreId"
                @change="loadProducts"
        >
          <option v-for="(store, index) in stores" :key="index" :value="store.storeId">{{ store.storeName }}</option>
        </select>
      </div>
      <div class="w-full md:w-1/3 px-3">
        <label 
          class="
              block 
              uppercase 
              tracking-wide 
              text-xs 
              font-bold 
              mb-2
              text-gray-900
          "
        >
            From Warehouse
        </label>
        <select class="
                appearance-none
                block
                w-full
                bg-white
                text-gray-700
                border
                border-gray-400
                rounded
                py-3
                px-4
                leading-tight
                focus:outline-none
                focus:bg-white
                focus:border-gray-500
                "
                v-model="selWarehouseId"
        >
          <option value="">Select warehouse...</option>
          <option v-for="warehouse in warehouses" :key="warehouse.warehouseId" :value="warehouse.warehouseId">{{ warehouse.warehouseName }}</option>
        </select>
      </div>
      <div class="w-full md:w-1/3 px-3">
        <TextInput 
            label="Ship To Name"
            id="ship-to-name"
            placeholder="Amazon FBA"
            v-model:input="shipToName"
            inputType="text"
        />
      </div>
    </div>

    <div class="flex flex-wrap mt-4 mb-6">
      <div class="w-full px-3">
        <label 
          class="
              block 
              uppercase 
              tracking-wide 
              text-xs 
              font-bold 
              mb-2
              text-gray-900
          "
        >
            Product
        </label>
        <select class="
                appearance-none
                block
                w-full
                bg-white
                text-gray-700
                border
                border-gray-400
                rounded
                py-3
                px-4
                leading-tight
                focus:outline-none
                focus:bg-white
                focus:border-gray-500
                "
                v-model="selectedProduct"
        >
          <option value="">Select a product...</option>
          <option v-for="product in products" :key="product.productId" :value="product">
            {{ product.name }} ({{ product.sku }})
          </option>
        </select>
      </div>
    </div>

    <div class="flex flex-wrap mt-4 mb-6">
      <div class="w-full md:w-1/3 px-3">
        <TextInput 
            label="Total Quantity"
            id="total-qty"
            placeholder="80"
            v-model:input="totalQty"
            inputType="number"
            @input="calculateCases"
            @keyup.enter="focusQtyPerCase"
        />
      </div>
      <div class="w-full md:w-1/3 px-3">
        <TextInput 
            label="Quantity Per Case"
            id="qty-per-case"
            placeholder="40"
            v-model:input="qtyPerCase"
            inputType="number"
            @input="calculateCases"
            @keyup.enter="focusOrderNum"
        />
      </div>
      <div class="w-full md:w-1/3 px-3">
        <div class="mt-8">
          <span class="text-gray-700 font-bold">Number of Cases: {{ numCases }}</span>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap mt-4 mb-6">
      <div class="w-full px-3">
        <TextInput 
            label="Order Number"
            id="order-num"
            placeholder="FBA1234567"
            v-model:input="orderNum"
            inputType="text"
            @keyup.enter="focusFirstTracking"
        />
      </div>
    </div>

    <div v-if="numCases > 0" class="mt-8">
      <h3 class="text-lg font-bold mb-4">Tracking Numbers</h3>
      <div v-for="(caseNum, index) in numCases" :key="index" class="flex flex-wrap mb-4">
        <div class="w-full px-3">
          <TextInput 
              :label="orderNum + 'BOX' + (index + 1) + ' (' + getQuantityForCase(index) + ' units)'"
              :id="'tracking-' + index"
              placeholder="Scan tracking number"
              v-model:input="trackingNumbers[index]"
              inputType="text"
              @keyup.enter="focusNextTracking(index)"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-wrap mt-8 mb-6">
      <div class="w-full px-3">
        <SubmitFormButton 
            btnText="Create Orders"
            @click="submit"
            :disabled="!canSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import axios from 'axios'
  import { onMounted, ref, computed } from 'vue'
  import TextInput from '@/components/global/TextInput.vue'
  import SubmitFormButton from '@/components/global/SubmitFormButton.vue'

  let stores = ref([])
  let selStoreId = ref(null)
  let warehouses = ref([])
  let selWarehouseId = ref('')
  let shipToName = ref('Amazon FBA')
  let orderNum = ref('')
  let products = ref([])
  let selectedProduct = ref('')
  let totalQty = ref('')
  let qtyPerCase = ref('')
  let numCases = ref(0)
  let trackingNumbers = ref([])
  let successMessage = ref('')
  let errorMessage = ref('')

  const canSubmit = computed(() => {
    return orderNum.value && 
           selStoreId.value &&
           selWarehouseId.value &&
           selectedProduct.value && 
           totalQty.value && 
           qtyPerCase.value && 
           shipToName.value &&
           numCases.value > 0 &&
           trackingNumbers.value.length === numCases.value &&
           trackingNumbers.value.every(tn => tn.trim() !== '')
  })

  onMounted(async () => {
      await getStores()
      await getWarehouses()
      
      // Auto-select store if only one contains "manual" (case insensitive)
      const manualStores = stores.value.filter(store => 
        store.storeName.toLowerCase().includes('manual')
      )
      if (manualStores.length === 1) {
        selStoreId.value = manualStores[0].storeId
        await loadProducts()
      } else if (stores.value.length === 1) {
        // Fallback: auto-select if only one store total
        selStoreId.value = stores.value[0].storeId
        await loadProducts()
      }
      
      // Auto-select warehouse if only one
      if (warehouses.value.length === 1) {
        selWarehouseId.value = warehouses.value[0].warehouseId
      }
  })

  const getStores = async () => {
    try {
      let res = await axios.get(process.env.VUE_APP_API_URL + 'api/v1/stores')
      stores.value = res.data
    } catch (err) {
      console.error('Error loading stores:', err)
    }
  }

  const getWarehouses = async () => {
    try {
      let res = await axios.get(process.env.VUE_APP_API_URL + 'api/v1/warehouses')
      warehouses.value = res.data || []
    } catch (err) {
      console.error('Error loading warehouses:', err)
      warehouses.value = []
    }
  }

  const loadProducts = async () => {
    if (!selStoreId.value) return
    
    try {
      let res = await axios.get(process.env.VUE_APP_API_URL + 'api/v1/products/' + selStoreId.value)
      products.value = res.data.products || []
    } catch (err) {
      console.error('Error loading products:', err)
      products.value = []
    }
  }

  const calculateCases = () => {
    if (totalQty.value && qtyPerCase.value) {
      numCases.value = Math.ceil(parseInt(String(totalQty.value)) / parseInt(String(qtyPerCase.value)))
      // Initialize tracking numbers array
      trackingNumbers.value = new Array(numCases.value).fill('')
    } else {
      numCases.value = 0
      trackingNumbers.value = []
    }
  }

  const getQuantityForCase = (caseIndex) => {
    if (!totalQty.value || !qtyPerCase.value) return 0
    
    const total = parseInt(String(totalQty.value))
    const perCase = parseInt(String(qtyPerCase.value))
    const remainingQty = total - (caseIndex * perCase)
    return Math.min(perCase, remainingQty)
  }

  const focusQtyPerCase = () => {
    setTimeout(() => {
      document.getElementById('qty-per-case').focus()
    }, 100)
  }

  const focusOrderNum = () => {
    setTimeout(() => {
      document.getElementById('order-num').focus()
    }, 100)
  }

  const focusFirstTracking = () => {
    if (numCases.value > 0) {
      setTimeout(() => {
        document.getElementById('tracking-0').focus()
      }, 100)
    }
  }

  const focusNextTracking = (currentIndex) => {
    const nextIndex = currentIndex + 1
    if (nextIndex < numCases.value) {
      setTimeout(() => {
        document.getElementById('tracking-' + nextIndex).focus()
      }, 100)
    } else {
      // If this is the last tracking number, focus the submit button
      setTimeout(() => {
        const submitButton = document.querySelector('button[type="button"]')
        if (submitButton) submitButton.focus()
      }, 100)
    }
  }

  const submit = async () => {
    if (!canSubmit.value) return

    try {
      errorMessage.value = ''
      successMessage.value = ''

      // Filter out empty tracking numbers and ensure we have the right count
      const cleanTrackingNumbers = trackingNumbers.value.filter(tn => tn && tn.trim() !== '')
      
      let res = await axios.post(process.env.VUE_APP_API_URL + 'api/v1/create_manual_orders', {
        orderNumber: orderNum.value,
        storeId: selStoreId.value,
        warehouseId: selWarehouseId.value,
        productSku: selectedProduct.value.sku,
        productName: selectedProduct.value.name,
        totalQty: parseInt(totalQty.value),
        qtyPerCase: parseInt(qtyPerCase.value),
        shipToName: shipToName.value,
        trackingNumbers: cleanTrackingNumbers
      })

      if (res.data.success) {
        successMessage.value = res.data.message
        // Reset form
        orderNum.value = ''
        selectedProduct.value = ''
        totalQty.value = ''
        qtyPerCase.value = ''
        shipToName.value = 'Amazon FBA'
        selWarehouseId.value = ''
        numCases.value = 0
        trackingNumbers.value = []
      } else {
        errorMessage.value = res.data.error || 'An error occurred'
      }
    } catch (err) {
      errorMessage.value = err.response?.data?.error || 'An error occurred'
    }
  }
</script>
